import {
  GuestSummary,
  CheckInTransaction,
  PersonInfo,
  EventGroup,
  GuestStatus,
  SeatingAssignment,
  GuestEmail,
} from "sharedTypes"
import { sortPlusOnes } from "utilities/arrays"
import {
  SORT_OPTIONS, ColumnId, ColumnConfig,
} from "./columnsConfig"

export type ExtendedGuestSummary = GuestSummary & {
  isPlusOne?: boolean;
}

export interface ColumnValueMap {
  name: PersonInfo
  email: string
  phone: string
  city: string
  country: string
  companyName: string
  companyTitle: string
  emailSubscription: boolean
  whatsappOptIn: boolean
  tags: EventGroup[]
  confirmationStatus: GuestStatus
  emailValidity: {
    email: string
    emailHardBounced: boolean
    emailSoftBounced: boolean
  }
  emailCampaign: { emails: GuestEmail[] }
  emailsSent: { emails: GuestEmail[]; guestId: string }
  plusOnesCount: number
  createdTime: string
  lastModified: string
  confirmedTime: string
  checkInStatus: {
    checkedIn: number
    transactions: CheckInTransaction[]
  }
  seat: {
    seatingAssignment: SeatingAssignment
    standing: boolean
  }
}

export const findCurrentSortOption = (currentOrder: string) => {
  if (!currentOrder) return null

  return SORT_OPTIONS.find((opt) => opt.serverOrder === currentOrder) || null
}

export const getNextSortOrder = (columnId: string, currentOrder: string): string | null => {
  if (!columnId) return null

  const options = SORT_OPTIONS.filter(
    (opt) => opt.columnId === columnId && opt.headerCycle,
  )
  if (!options.length) return null

  const currentIndex = options.findIndex((opt) => opt.serverOrder === currentOrder)

  // If not currently sorted by this column, start with first option
  if (currentIndex === -1) return options[0]?.serverOrder || null

  // Cycle through all options, then clear
  if (currentIndex < options.length - 1) return options[currentIndex + 1]?.serverOrder || null

  return null
}

export const flattenGuestsWithPlusOnes = (guests: GuestSummary[], showPlusOnes = true):
ExtendedGuestSummary[] => {
  if (!showPlusOnes) return guests as ExtendedGuestSummary[]

  return guests.reduce((acc: ExtendedGuestSummary[], guest) => {
    acc.push(guest as ExtendedGuestSummary)
    if (guest.plusOnes && guest.plusOnes.length > 0) {
      const sortedPlusOnes = sortPlusOnes(guest.plusOnes)
      sortedPlusOnes.forEach((plusOne) => {
        acc.push({
          ...plusOne,
          isPlusOne: true,
        })
      })
    }

    return acc
  }, [])
}

export const calculateColumnWidths = (
  visibleColumns: string[],
  configs: Record<string, ColumnConfig>,
  containerWidth: number,
  columnSizing: Record<string, number> = {},
): { widths: Record<string, number>; totalWidth: number; shouldScroll: boolean } => {
  const safeContainerWidth = Math.max(0, containerWidth || 0)

  const getWidth = (colId: string) => columnSizing[colId] || configs[colId]?.defaultWidth || 150

  const totalDefaultWidth = visibleColumns
    .reduce((sum, colId) => sum + getWidth(colId), 0)

  if (totalDefaultWidth >= safeContainerWidth || safeContainerWidth === 0) {
    return {
      widths: visibleColumns.reduce((acc, colId) => {
        const column = configs[colId]
        if (!column) return acc
        acc[colId] = getWidth(colId)

        return acc
      }, {} as Record<string, number>),
      totalWidth: totalDefaultWidth,
      shouldScroll: totalDefaultWidth > safeContainerWidth,
    }
  }

  // Only distribute extra space among columns that don't have user-set sizes
  const userSizedTotal = visibleColumns
    .filter((colId) => columnSizing[colId])
    .reduce((sum, colId) => sum + columnSizing[colId], 0)
  const autoColumns = visibleColumns.filter((colId) => !columnSizing[colId])
  const autoDefaultTotal = autoColumns
    .reduce((sum, colId) => sum + (configs[colId]?.defaultWidth || 150), 0)
  const extraSpace = safeContainerWidth - userSizedTotal - autoDefaultTotal

  const widths: Record<string, number> = {}
  let distributedTotal = 0

  visibleColumns.forEach((colId) => {
    if (columnSizing[colId]) {
      widths[colId] = columnSizing[colId]
      distributedTotal += widths[colId]
    } else {
      const column = configs[colId]
      if (!column) return
      const defaultW = column.defaultWidth
      const expanded = autoDefaultTotal > 0
        ? Math.floor(defaultW + (defaultW / autoDefaultTotal) * Math.max(0, extraSpace))
        : defaultW
      widths[colId] = expanded
      distributedTotal += expanded
    }
  })

  // Fix rounding remainder by adjusting the first auto-sized column
  const remainder = safeContainerWidth - distributedTotal
  if (remainder !== 0) {
    const firstAutoCol = autoColumns[0] || visibleColumns[0]
    if (firstAutoCol && widths[firstAutoCol] !== undefined) {
      widths[firstAutoCol] += remainder
    }
  }

  return {
    widths,
    totalWidth: Math.max(safeContainerWidth, distributedTotal),
    shouldScroll: distributedTotal > safeContainerWidth,
  }
}

export const columnAccessors = {
  name: (row: GuestSummary) => row.personInfo,
  email: (row: GuestSummary) => row.personInfo?.email ?? "",
  phone: (row: GuestSummary) => row.personInfo?.phone ?? "",
  city: (row: GuestSummary) => row.personInfo?.address?.city ?? "",
  country: (row: GuestSummary) => row.personInfo?.address?.country ?? "",
  companyName: (row: GuestSummary) => row.personInfo?.companyName ?? "",
  companyTitle: (row: GuestSummary) => row.personInfo?.companyTitle ?? "",
  emailSubscription: (row: GuestSummary) => row.personInfo?.unsubscribed ?? false,
  whatsappOptIn: (row: GuestSummary) => row.personInfo?.optedInWhatsapp ?? false,
  tags: (row: GuestSummary) => row.eventGroups ?? [],
  confirmationStatus: (row: GuestSummary) => row.status,
  emailValidity: (row: GuestSummary) => ({
    email: row.personInfo.email,
    emailHardBounced: row.personInfo.emailHardBounced,
    emailSoftBounced: row.personInfo.emailSoftBounced,
  }),
  emailCampaign: (row: GuestSummary) => ({ emails: row.emails ?? [] }),
  emailsSent: (row: GuestSummary) => ({ emails: row.emails ?? [], guestId: row.id }),
  plusOnesCount: (row: GuestSummary) => row.groupSize,
  createdTime: (row: GuestSummary) => row.createdTime,
  lastModified: (row: GuestSummary) => row.updatedAt,
  confirmedTime: (row: GuestSummary) => row.confirmedTime,
  checkInStatus: (row: GuestSummary) => ({
    checkedIn: row.checkedIn,
    transactions: (row.personInfo?.transactions ?? []) as CheckInTransaction[],
  }),
  seat: (row: GuestSummary) => ({
    seatingAssignment: row.seatingAssignment,
    standing: row.standing,
  }),
}

export const getColumnAccessor = <K extends ColumnId>
  (columnId: K) => columnAccessors[columnId as ColumnId]

export const TABLE_HEADER_HEIGHT_PX = 64
export const ROW_HEIGHT = 60
export const OVERSCAN_COUNT = 5
export const CELL_PADDING_X = 3
